import React, { useMemo, useState, useLayoutEffect, useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Icon,
  MenuItem,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles';
import { red, common } from '@mui/material/colors'
import { MatxMenu } from 'app/components'
import IconButton from '@mui/material/IconButton'
import ChartTooltip from '../ChartTooltip'

// A feature in DEL datasets
const USED_IN_PREDICTON_FEATURE = 'Used in Prediction'
const LIBRARY_NAME_FEATURE_FEATURE = 'LIBRARY_NAME'
const TARGET_ENR_FEATURE = 'TARGET_ENR'

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
}))
const options = {
  animation: true,
  cutoutPercentage: 80,
  layout: { padding: 0 },
  labels: {
    display: false,
    labels: {
      color: 'rgb(255, 99, 132)',
    },
  },
  plugins: {
    legend: {
      display: false,
    },
  },
  maintainAspectRatio: false,
  responsive: true,
  tooltips: {
    backgroundColor: red[500],
    bodyFontColor: common.white,
    borderColor: common.white,
    borderWidth: 1,
    enabled: false,
    footerFontColor: common.white,
    intersect: false,
    mode: 'index',
    titleFontColor: common.white,
  },
}

const StatsBar = ({
  className,
  features = {},
  featuresLabels = {},
  fetchStats = async () => {},
  ...rest
}) => {
  const chartContainerRef = useRef(null)
  const classes = useStyles()
  const [selectedFeature, setSelectedFeature] = useState(undefined)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [labels, setLabels] = useState([])

  const getFeatureLabel = (feature) => {
    const featureLabel = featuresLabels[feature] || feature
    return String(featureLabel).split('_').length >= 2
      ? featureLabel.split('_')[0] +
          '(' +
          featureLabel.replace(featureLabel.split('_')[0] + '_', '') +
          ')'
      : featureLabel
  }

  const featuresMenuItems = useMemo(() => {
    if (!features) return []
    const filtered = []
    Object.keys(features).forEach((feature) => {
      if (features[feature].predictable)
        filtered.push({ feature, label: getFeatureLabel(feature) })
    })
    return filtered.sort((a, b) => a.label.localeCompare(b.label))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features])

  const getDefaultFeatureToSelect = (featuresMenuItems) => {
    let defaultFeature = featuresMenuItems.find(
      (featureMenuItem) =>
        featureMenuItem.feature.toUpperCase() ===
        LIBRARY_NAME_FEATURE_FEATURE.toUpperCase()
    )

    if (!defaultFeature) {
      defaultFeature = featuresMenuItems.find(
        (featureMenuItem) =>
          featureMenuItem.feature.toUpperCase() ===
          TARGET_ENR_FEATURE.toUpperCase()
      )
    }

    if (!defaultFeature) {
      defaultFeature = featuresMenuItems.find(
        (featureMenuItem) =>
          featureMenuItem.feature.toUpperCase() ===
          USED_IN_PREDICTON_FEATURE.toUpperCase()
      )
    }

    return defaultFeature
  }

  useLayoutEffect(() => {
    let selectedFeature = undefined
    // Auto select the feature 'Used in Predictoin' if it exists
    if (featuresMenuItems.length) {
      selectedFeature = getDefaultFeatureToSelect(featuresMenuItems)
      selectedFeature = selectedFeature
        ? selectedFeature.feature
        : featuresMenuItems[0].feature
    }

    setSelectedFeature(selectedFeature)
  }, [featuresMenuItems])

  useLayoutEffect(() => {
    setData([])
    setLabels([])
    if (selectedFeature) {
      setLoading(true)
      const feature = selectedFeature
      fetchStats(feature)
        .then((res = []) => {
          if (feature !== selectedFeature) return
          const _labels = []
          const _data = []
          const _featureType = features[selectedFeature].dataType

          if (_featureType === 'STRING' || _featureType === 'INTEGER') {
            res.forEach((item) => {
              _labels.push(item.label)
              _data.push(item.count)
            })
          } else {
            res.forEach((item) => {
              _labels.push(item.min)
              _data.push(item.count)
            })
          }
          setData(_data)
          setLabels(_labels)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [selectedFeature])

  const barData = useMemo(
    () => ({
      datasets: [
        {
          data,
          borderWidth: 2,
          borderColor: common.white,
          backgroundColor: red[500],
          hoverBorderColor: common.white,
          label: 'Count',
        },
      ],
      labels,
    }),
    [data, labels]
  )

  const handleClick = (feature) => {
    setSelectedFeature(feature)
  }

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <strong>
        <CardHeader
          title={getFeatureLabel(selectedFeature)}
          titleTypographyProps={{ variant: 'inherit' }}
          action={
            !loading && (
              <MatxMenu
                menuButton={
                  <IconButton size="large">
                    <Icon className="cursor-pointer">more_horiz</Icon>
                  </IconButton>
                }
              >
                {featuresMenuItems.map(({ feature, label }, index) => {
                  const onClick = () => {
                    handleClick(feature)
                  }
                  return (
                    <div key={`${index}-${feature}`} onClick={onClick}>
                      <MenuItem>{label}</MenuItem>
                    </div>
                  )
                })}
              </MatxMenu>
            )
          }
        />
      </strong>
      <Divider variant="middle" />
      <CardContent>
        {!loading && <ChartTooltip chartContainerRef={chartContainerRef} />}
        <Box ref={chartContainerRef} height={148} position="relative">
          {loading ? (
            <div className={'flex justify-center'}>
              <CircularProgress />
            </div>
          ) : (
            <Bar data={barData} options={options} />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

StatsBar.propTypes = {
  className: PropTypes.string,
  features: PropTypes.object,
  fetchStats: PropTypes.func,
}

export default StatsBar
